<script setup lang="ts">
import { reactive, ref } from "vue";
import type { FormInstance, FormRules } from "element-plus";
import router from "../../router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "../../store/authStore";

const store = useAuthStore();
const token = localStorage.getItem("_panda");
axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

interface RuleForm { old_password: string; new_password: string; repeat_password: string; }

const validateConfirmPassword = (rule: any, value: any, callback: any) => {
  if (value === "") callback(new Error("Please confirm your password"));
  else if (value !== ruleForm.new_password) callback(new Error("Passwords do not match"));
  else callback();
};

const loading = ref(false);
const formError = ref("");
const showOld = ref(false);
const showNew = ref(false);
const showConfirm = ref(false);
const ruleFormRef = ref<FormInstance>();
const ruleForm = reactive<RuleForm>({ old_password: "", new_password: "", repeat_password: "" });

const rules = reactive<FormRules<RuleForm>>({
  old_password: [{ required: true, message: "Please enter your current password", trigger: "blur" }],
  new_password: [{ required: true, message: "Please enter a new password", trigger: "blur" }, { min: 6, message: "Password must be at least 6 characters", trigger: "blur" }],
  repeat_password: [{ validator: validateConfirmPassword, trigger: "blur" }],
});

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid) => { if (valid) { loading.value = true; postData(); } });
};

const postData = async () => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_ONBOARDING_SERVICE}changepassword?token=${store.getToken}`,
      { old_password: ruleForm.old_password, new_password: ruleForm.new_password, company_id: store.getUserData.company_id, user_id: store.getUserData.user_id }
    );
    loading.value = false;
    if (response.status == 200 && response.data.status) {
      ElMessage({ message: "Password updated successfully", type: "success" });
      router.push("/request");
    } else {
      ElMessage({ message: response.data.message || "Password update failed.", type: "error" });
    }
  } catch {
    loading.value = false;
    formError.value = "Something went wrong, please try again.";
  }
};

const logwat = () => {
  store.setAuthStatus(false); store.$reset();
  localStorage.removeItem("_panda"); router.push("/login");
};
</script>

<template>
  <div class="fp-page">
    <div class="fp-inner">
      <div class="fp-card" v-loading="loading">

        <div class="fp-card-header">
          <div class="fp-card-icon fp-card-icon--amber">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <div>
            <h1 class="fp-title">Change Password</h1>
            <p class="fp-subtitle">Keep your account secure with a strong password</p>
          </div>
        </div>

        <div class="fp-error" v-if="formError">{{ formError }}</div>

        <el-form ref="ruleFormRef" :model="ruleForm" :rules="rules" label-width="0px" status-icon>

          <div class="fp-field">
            <label class="fp-label">Current Password <span class="fp-required">*</span></label>
            <el-form-item prop="old_password" class="fp-form-item">
              <div class="fp-input-wrap">
                <input v-model="ruleForm.old_password" class="fp-input" :type="showOld ? 'text' : 'password'" placeholder="Enter current password" autocomplete="current-password" />
                <button type="button" class="fp-eye" @click="showOld = !showOld">
                  <svg v-if="!showOld" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </el-form-item>
          </div>

          <div class="fp-divider"></div>

          <div class="fp-field">
            <label class="fp-label">New Password <span class="fp-required">*</span></label>
            <el-form-item prop="new_password" class="fp-form-item">
              <div class="fp-input-wrap">
                <input v-model="ruleForm.new_password" class="fp-input" :type="showNew ? 'text' : 'password'" placeholder="At least 6 characters" autocomplete="new-password" />
                <button type="button" class="fp-eye" @click="showNew = !showNew">
                  <svg v-if="!showNew" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </el-form-item>
          </div>

          <div class="fp-field">
            <label class="fp-label">Confirm New Password <span class="fp-required">*</span></label>
            <el-form-item prop="repeat_password" class="fp-form-item">
              <div class="fp-input-wrap">
                <input v-model="ruleForm.repeat_password" class="fp-input" :type="showConfirm ? 'text' : 'password'" placeholder="Repeat new password" autocomplete="new-password" />
                <button type="button" class="fp-eye" @click="showConfirm = !showConfirm">
                  <svg v-if="!showConfirm" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
            </el-form-item>
          </div>

          <button class="fp-submit-btn" @click.prevent="submitForm(ruleFormRef)" :disabled="loading">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Update Password
          </button>

        </el-form>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }
.fp-page { font-family: 'DM Sans', sans-serif; background: #f8fafc; min-height: 100%; display: flex; align-items: flex-start; justify-content: center; padding: 48px 24px; }
.fp-inner { width: 100%; max-width: 480px; }
.fp-card { background: white; border-radius: 16px; border: 1.5px solid #e2e8f0; padding: 32px; box-shadow: 0 4px 24px rgba(18,40,74,0.07); }
.fp-card-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 28px; }
.fp-card-icon { width: 44px; height: 44px; border-radius: 12px; background: #eff6ff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.fp-card-icon--amber { background: #fffbeb; }
.fp-title { font-size: 1.15rem; font-weight: 700; color: #12284A; margin: 0 0 3px; }
.fp-subtitle { font-size: 0.8rem; color: #94a3b8; margin: 0; }
.fp-error { background: #fef2f2; border: 1.5px solid #fca5a5; color: #dc2626; border-radius: 8px; padding: 10px 14px; font-size: 0.82rem; font-weight: 500; margin-bottom: 20px; }
.fp-divider { height: 1px; background: #f1f5f9; margin: 4px -32px 18px; }
.fp-field { margin-bottom: 16px; }
.fp-label { display: block; font-size: 0.78rem; font-weight: 600; color: #475569; margin-bottom: 6px; }
.fp-required { color: #dc2626; }
.fp-form-item { margin-bottom: 0 !important; }
:deep(.fp-form-item .ep-form-item__content) { display: block; width: 100%; }
:deep(.fp-form-item .ep-form-item__error) { padding-top: 4px; font-size: 0.74rem; }
.fp-input-wrap { position: relative; display: flex; align-items: center; width: 100%; }
.fp-input {
  width: 100%; height: 42px; padding: 0 42px 0 14px;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.87rem; color: #1e293b;
  outline: none; transition: border-color 0.15s, box-shadow 0.15s; background: white;
}
.fp-input:focus { border-color: #12284A; box-shadow: 0 0 0 3px rgba(18,40,74,0.08); }
.fp-input::placeholder { color: #94a3b8; }
.fp-eye {
  position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: #94a3b8; cursor: pointer; padding: 2px;
  display: flex; align-items: center; transition: color 0.15s;
}
.fp-eye:hover { color: #475569; }
.fp-submit-btn {
  width: 100%; height: 44px; margin-top: 10px;
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  color: white; border: none; border-radius: 10px;
  font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: opacity 0.15s;
}
.fp-submit-btn:hover:not(:disabled) { opacity: 0.9; }
.fp-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>